import { useQuery, useMutation, useQueryClient, queryOptions } from '@tanstack/react-query';
import { api } from '../api';
import { toast } from '../toast';
import type { SubscriptionResponse, MakeUpCreditResponse, ResignStudentGroupRequest } from '@sportschool/shared-types';

/** Query options of `useStudentSubscriptions` — shared with the offline prefetch. */
export function studentSubscriptionsQueryOptions(schoolId: string, studentMemberId: string) {
  return queryOptions({
    queryKey: ['subscriptions', schoolId, studentMemberId],
    queryFn: async (): Promise<SubscriptionResponse[]> => {
      const res = await api.get(`/schools/${schoolId}/subscriptions/student/${studentMemberId}`);
      return res.data;
    },
    enabled: !!schoolId && !!studentMemberId,
  });
}

export function useStudentSubscriptions(schoolId: string, studentMemberId: string) {
  return useQuery(studentSubscriptionsQueryOptions(schoolId, studentMemberId));
}

export function useOptOutSubscription(schoolId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (subscriptionId: string) => {
      await api.post(`/schools/${schoolId}/subscriptions/${subscriptionId}/opt-out`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['subscriptions', schoolId] });
      qc.invalidateQueries({ queryKey: [`/api/v1/schools/${schoolId}/me`] });
      toast.success('Zrezygnowano z odnawiania subskrypcji', 'toasts.subscription_opt_out_success');
    },
    onError: () => toast.error('Nie udało się zrezygnować z subskrypcji', 'toasts.subscription_opt_out_failed'),
  });
}

export function useReactivateSubscription(schoolId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (subscriptionId: string) => {
      await api.post(`/schools/${schoolId}/subscriptions/${subscriptionId}/reactivate`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['subscriptions', schoolId] });
      qc.invalidateQueries({ queryKey: [`/api/v1/schools/${schoolId}/me`] });
      toast.success('Przywrócono odnawianie subskrypcji', 'toasts.subscription_reactivate_success');
    },
    onError: () => toast.error('Nie udało się przywrócić subskrypcji', 'toasts.subscription_reactivate_failed'),
  });
}

export function useResignStudentGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ schoolId, data }: { schoolId: string; data: ResignStudentGroupRequest }) => {
      const res = await api.post(`/schools/${schoolId}/enrollments/groups/resign`, data);
      return res.data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ['enrollments', variables.schoolId] });
      qc.invalidateQueries({ queryKey: ['subscriptions', variables.schoolId] });
      qc.invalidateQueries({ queryKey: ['bills', variables.schoolId] });
    },
  });
}

// ---- MakeUp Credits ----

/** Query options of `useStudentCredits` — shared with the offline prefetch. */
export function studentCreditsQueryOptions(schoolId: string, studentMemberId: string) {
  return queryOptions({
    queryKey: ['credits', schoolId, studentMemberId],
    queryFn: async (): Promise<MakeUpCreditResponse[]> => {
      const res = await api.get(`/schools/${schoolId}/credits/student/${studentMemberId}`);
      return res.data;
    },
    enabled: !!schoolId && !!studentMemberId,
  });
}

export function useStudentCredits(schoolId: string, studentMemberId: string) {
  return useQuery(studentCreditsQueryOptions(schoolId, studentMemberId));
}

export function useReportAbsence(schoolId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (enrollmentId: string) => {
      const res = await api.post(`/schools/${schoolId}/credits/report-absence`, { enrollmentId });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['enrollments', schoolId] });
      qc.invalidateQueries({ queryKey: ['sessions', schoolId] });
      qc.invalidateQueries({ queryKey: ['credits', schoolId] });
      qc.invalidateQueries({ predicate: (q) => typeof q.queryKey[0] === 'string' && q.queryKey[0].includes(`/schools/${schoolId}/credits`) });
      qc.invalidateQueries({ queryKey: [`/api/v1/schools/${schoolId}/me`] });
      toast.success('Nieobecność została zgłoszona', 'toasts.absence_reported');
    },
    onError: () => toast.error('Nie udało się zgłosić nieobecności', 'toasts.absence_report_failed'),
  });
}

export function useCancelAbsence(schoolId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (enrollmentId: string) => {
      const res = await api.post(`/schools/${schoolId}/credits/cancel-absence`, { enrollmentId });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['enrollments', schoolId] });
      qc.invalidateQueries({ queryKey: ['sessions', schoolId] });
      qc.invalidateQueries({ queryKey: ['credits', schoolId] });
      qc.invalidateQueries({ predicate: (q) => typeof q.queryKey[0] === 'string' && q.queryKey[0].includes(`/schools/${schoolId}/credits`) });
      qc.invalidateQueries({ queryKey: [`/api/v1/schools/${schoolId}/me`] });
      toast.success('Cofnięto nieobecność', 'toasts.absence_cancelled');
    },
    onError: (error: any) => {
      const data = error.response?.data;
      const serverDetail = data?.detail || data?.Detail;
      const detail = serverDetail || 'Nie udało się cofnąć nieobecności';
      // The generic message is only a fallback: it must not hide the server's explanation
      const code = data?.code || data?.Code || (serverDetail ? undefined : 'toasts.absence_cancel_failed');
      const args = data?.args || data?.Args;
      toast.error(detail, code, args);
    },
  });
}
